import createOneIdWidgetScript from "@/frontend/lib/createOneIdWidgetScript";
import {AgeVerification as OneIdAgeVerification} from "@/frontend/lib/oneId/ageVerification";
import {AgeVerification as TuCheckAgeVerification} from "@/frontend/lib/tuCheck/ageVerification";
import TuVerificationStorage from "@/frontend/lib/tuCheck/storage";
import {api} from "@/frontend/lib/api";
import FormInput from "@/frontend/pre-checkout/components/FormInput";
import FormSelect from "@/frontend/pre-checkout/components/FormSelect";
import {useForm} from "@/frontend/pre-checkout/lib/Form";
import {Cart, Checkout, TuVerifiedAddress} from "@/frontend/types";
import {WidgetShop, WidgetVerification} from "@/types/frontend";
import axios from "axios";
import qs from "qs";
import React, {useEffect, useState} from "react";
import FormAddressLookup from "./components/FormAddressLookup";
import {InstallationMethods} from "@/enums";
import base64urlSafe from "@/frontend/lib/base64urlSafe";
import {result, unescape} from "lodash";
import {v4 as uuidv4} from 'uuid';

function App() {
    const formatFormData = () => ({
        shop_domain: window.oneidLiquid.shop_domain,
        oneid_over_18: window.oneidLiquid.oneid_over_18,
        email: window.oneidLiquid.customer.email,
        first_name: window.oneidLiquid.customer.first_name,
        last_name: window.oneidLiquid.customer.last_name,
        address1: window.oneidLiquid.customer.default_address.address1,
        address2: window.oneidLiquid.customer.default_address.address2,
        city: window.oneidLiquid.customer.default_address.city,
        zip: window.oneidLiquid.customer.default_address.zip,
        country_code: window.oneidLiquid.customer.default_address.country_code,
        validated_address_id: "",
    });
    const form = useForm<Checkout>(formatFormData());
    const [cart, setCart] = useState<Cart>();
    const [shop, setShop] = useState<WidgetShop>();
    const [loading, setLoading] = useState(true);
    const installMethod = "TU Pre-checkout";
    const [verifiedAddress, setVerifiedAddress] = useState<TuVerifiedAddress>();
    const [showOneIdFailedNotification, setShowOneIdFailedNotification] = useState(false);
    const [indeterminateNotification, setIndeterminateNotification] = useState(false);
    const [customToken, setCustomToken] = useState('one-id-' + uuidv4());
    useEffect(() => {
        (async function () {
            await preFlight();
            setLoading(false);
        })();
    }, []);

    useEffect(() => {
        if (form.fields.country_code && form.fields.country_code != "GB") {
            continueToCheckout();
        }
    }, [form.fields.country_code]);

    useEffect(() => {
        if (verifiedAddress) {
            form.withData({
                ...form.fields,
                address1: verifiedAddress.line1,
                address2: verifiedAddress.line2,
                city: verifiedAddress.city,
                country: verifiedAddress.postalCode,
                zip: verifiedAddress.postalCode,
                validated_address_id: verifiedAddress.id,
            });
        }
    }, [verifiedAddress]);

    const getCart = async (): Promise<Cart> => {
        const response = await axios.get("/cart.js");
        return response.data;
    };
    const addToCart = async (customToken: string): Promise<any> => {
        await axios.post('/cart/update.js', {
            attributes: {
                'OneID Token': customToken
            }
        }).then(data => console.log(data));
    };

    const preFlight = async () => {
        const shop = await api.getShop({
            shop_domain: window.oneidLiquid.shop_domain,
            install_method: installMethod,
        });
        setShop(shop);

        if (!shop || shop.install_method !== InstallationMethods.TU_PRE_CHECKOUT) {
            console.log("OneID Pre-checkout is inactive / disabled / exceeded limit!");
            continueToCheckout();
            return;
        }

        const cart = await getCart();
        setCart(cart);
        if(cart.attributes['OneID Token']){
            console.log('set custom token')
           setCustomToken(cart.attributes['OneID Token']);
        }
        const tuCheckAgeVerification = new TuCheckAgeVerification(shop, cart.token, customToken, 18);
        await tuCheckAgeVerification.initialise();

        if (!tuCheckAgeVerification.required()) {
            const data = TuVerificationStorage.get();
            if (data?.address?.line1) {
                form.withData({
                    ...form.fields,
                    email: data.email as string,
                    first_name: data.first_name as string,
                    last_name: data.last_name as string,
                    address1: data?.address?.line1,
                    address2: data?.address?.line2,
                    city: data?.address?.city,
                    country: data?.address?.postalCode,
                    zip: data?.address?.postalCode,
                });
            }

            if (tuCheckAgeVerification.passed()) {
                console.log("Tu Check passed, redirecting  to checkout");
                continueToCheckout();
            } else if (tuCheckAgeVerification.failed() && shop.failed_check_handling) {
                console.log("Tu Check failed");
                const oneIdAgeVerification = new OneIdAgeVerification(shop, cart.token, customToken,18);
                await oneIdAgeVerification.initialise();

                if (oneIdAgeVerification.passed()) {
                    console.log("OneID passed, redirecting  to checkout");
                    continueToCheckout();
                } else if (oneIdAgeVerification.required()) {
                    console.log("Requesting OneID AV Check");
                    injectOneIdWidget(shop, data?.state);
                } else if (oneIdAgeVerification.failed()) {
                    console.log("OneID failed");
                    setShowOneIdFailedNotification(true);
                } else {
                    console.log("OneID indeterminate response received");
                    setIndeterminateNotification(true);
                }
            } else {
                console.log("Tu Check failed, Skip One ID check");
                continueToCheckout();
            }
        }
    };

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setLoading(true);

        if (verifiedAddress && form.fields.validated_address_id) {
            addToCart(customToken).then(r =>  runTuCheck());
        }
    };
    const runTuCheck = async () => {
        console.log(window.location.href);
        try {
            const state = {
                cartToken: cart?.token,
                customToken: customToken,
                returnUrl: window.location.href,
                ageOver: 18,
                shop: shop!.shop_domain,
                requestType: "pre-checkout-age-check",
                checkoutAutoFill: false,
            };
            const tuCheck = await api.tuCheck({
                ...form.fields,
                cart_token: cart!.token,
                custom_token: customToken,
                state: base64urlSafe(JSON.stringify(state))
            });
            const data = {
                id: tuCheck.id,
                age_over: 18,
                cart_token: cart!.token,
                custom_token: customToken,
                verification_result: tuCheck.result,
                address: tuCheck.address,
                email: form.fields.email,
                first_name: form.fields.first_name,
                last_name: form.fields.last_name,
                state: tuCheck.state
            } as WidgetVerification;
            TuVerificationStorage.set(data);

            form.withData({
                ...form.fields,
                address1: tuCheck.address.line1,
                address2: tuCheck.address.line2,
                city: tuCheck.address.city,
                country: tuCheck.address.postalCode,
                zip: tuCheck.address.postalCode,
            });

            tuCheck.result === "passed" ? continueToCheckout() : injectOneIdWidget(shop, data.state);
        } catch (e) {
            console.error(e);
            setLoading(false);
        }
    };

    const continueToCheckout = async () => {
        const data = TuVerificationStorage.get();
        const query = {
            checkout: {
                email: data?.email || form.fields.email,
                shipping_address: {
                    first_name: data?.first_name || form.fields.first_name,
                    last_name: data?.last_name || form.fields.last_name,
                    address1: data?.address?.line1 || form.fields.address1,
                    address2: data?.address?.line2 || form.fields.address2,
                    city: data?.address?.city || form.fields.city,
                    country: data?.address?.countryIso2 || form.fields.country_code,
                    zip: data?.address?.postalCode || form.fields.postalCode,
                },
            },
        };

        // ISSUE: when revisited, address from localStorage is passed to the '/checkout' page but shopify ignores the address!

        window.location.replace("/checkout?" + qs.stringify(query, {encodeValuesOnly: true}));
    };

    const backToCart = async () => {
        window.location.replace("/cart");
    };

    const injectOneIdWidget = (shop, state) => {
        setLoading(false);
        if (!shop.failed_check_handling) {
            console.log("Tu Check failed, Skip One ID check");
            continueToCheckout();
            return;
        }
        const script = createOneIdWidgetScript(
            shop!.oneid_client_id,
            "age_over_18", {},
            "true",
            state
        );
        script.setAttribute("data-style", "none");
        script.onload = function () {
            window.oneid.showModal();
        };
        document.head.appendChild(script);
    };

    return (
        <div className="content">
            <div className="wrap">
                <div className="main">
                    <a className="shop-name font-extrabold" href="/">
                        {unescape(window.oneidLiquid.shop_name) || ""}
                    </a>
                    <div className="full-bleed mt-5 py-3">
                        <div className="flex justify-between items-center py-1">
                            <span className="flex text-[#1773b0]">
                                <span className="w-5 h-5 mr-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" focusable="false"
                                         aria-hidden="true">
                                        <circle cx="3.5" cy="11.9" r="0.3"></circle>
                                        <circle cx="10.5" cy="11.9" r="0.3"></circle>
                                        <path
                                            stroke="#1773b0"
                                            fill="transparent"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M3.502 11.898h-.004v.005h.004v-.005Zm7 0h-.005v.005h.005v-.005ZM1.4
                                            2.1h.865a.7.7 0 0 1 .676.516l1.818 6.668a.7.7 0 0 0 .676.516h5.218a.7.7
                                            0 0 0 .68-.53l1.05-4.2a.7.7 0 0 0-.68-.87H3.4"
                                        ></path>
                                    </svg>
                                </span>
                                Order total
                            </span>
                            <span className="text-lg">
                                {cart?.currency === "GBP" && "£"}
                                {(cart?.items_subtotal_price ? cart?.items_subtotal_price / 100 : 0).toFixed(2)}
                            </span>
                        </div>
                    </div>
                    <ol className="breadcrumbs">
                        <li>
                            <a href="/cart">
                                <span>Cart</span>
                                <svg viewBox="0 0 10 10">
                                    <path d="M2 1l1-1 4 4 1 1-1 1-4 4-1-1 4-4"/>
                                </svg>
                            </a>
                        </li>
                        <li className="current">
                            <span>
                                <span>Information</span>
                                <svg viewBox="0 0 10 10">
                                    <path d="M2 1l1-1 4 4 1 1-1 1-4 4-1-1 4-4"/>
                                </svg>
                            </span>
                        </li>
                        <li className="disabled">
                            <span>
                                <span>Shipping</span>
                                <svg viewBox="0 0 10 10">
                                    <path d="M2 1l1-1 4 4 1 1-1 1-4 4-1-1 4-4"/>
                                </svg>
                            </span>
                        </li>
                        <li className="disabled">
                            <span>
                                <span>Payment</span>
                            </span>
                        </li>
                    </ol>

                    <div className="bg-[#006290] text-white p-3 rounded-md mt-4 mb-4">
                        <svg
                            className="h-5 w-5 text-white inline-block mr-2"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            {" "}
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="16" x2="12" y2="12"/>
                            {" "}
                            <line x1="12" y1="8" x2="12.01" y2="8"/>
                        </svg>
                        You must be aged 18 years or older to be able to complete this purchase. An age check will be
                        completed during
                        checkout using OneID. By continuing you agree to OneID&reg;&apos;s{" "}
                        <a href="https://oneid.uk/oneid-privacy-notice" className="text-white underline" target="_blank"
                           rel="noreferrer">
                            Privacy Policy
                        </a>{" "}
                        and{" "}
                        <a href="https://oneid.uk/terms-and-conditions-individual" className="text-white underline"
                           target="_blank" rel="noreferrer">
                            T&amp;Cs
                        </a>
                        .
                    </div>

                    <form onSubmit={onSubmit}>
                        <div className="">
                            <h2 className="">Contact information</h2>
                            <div className="grid col-span-1 gap-[12px]">
                                <FormInput
                                    label="Email"
                                    type="email"
                                    name="email"
                                    id="email"
                                    autoComplete="email"
                                    value={form.fields.email}
                                    required={true}
                                    onChange={form.handleChange("email")}
                                />

                                <div className="grid grid-cols-2 gap-[12px]">
                                    <FormInput
                                        label="First name"
                                        type="text"
                                        name="first_name"
                                        id="first_name"
                                        autoComplete="given-name"
                                        required={true}
                                        value={form.fields.first_name}
                                        onChange={form.handleChange("first_name")}
                                    />
                                    <FormInput
                                        label="Last name"
                                        type="text"
                                        name="last_name"
                                        id="last_name"
                                        autoComplete="family-name"
                                        required={true}
                                        aria-required="true"
                                        value={form.fields.last_name}
                                        onChange={form.handleChange("last_name")}
                                    />
                                </div>
                            </div>

                            <div className="spacer">&nbsp;</div>

                            <h2>Address</h2>

                            <div className="grid col-span-1 gap-[12px]">
                                <FormSelect
                                    label="Country/Region"
                                    name="country_code"
                                    id="country_code"
                                    autoComplete="country"
                                    value={form.fields.country_code}
                                    onChange={form.handleChange("country_code")}
                                >
                                    <option value="GB" id="country-0">
                                        United Kingdom
                                    </option>
                                    <option disabled={true} value="">
                                        ---
                                    </option>
                                    <option value="AF">Afghanistan</option>
                                    <option value="AX">Åland Islands</option>
                                    <option value="AL">Albania</option>
                                    <option value="DZ">Algeria</option>
                                    <option value="AD">Andorra</option>
                                    <option value="AO">Angola</option>
                                    <option value="AI">Anguilla</option>
                                    <option value="AG">Antigua &amp; Barbuda</option>
                                    <option value="AR">Argentina</option>
                                    <option value="AM">Armenia</option>
                                    <option value="AW">Aruba</option>
                                    <option value="AC">Ascension Island</option>
                                    <option value="AU">Australia</option>
                                    <option value="AT">Austria</option>
                                    <option value="AZ">Azerbaijan</option>
                                    <option value="BS">Bahamas</option>
                                    <option value="BH">Bahrain</option>
                                    <option value="BD">Bangladesh</option>
                                    <option value="BB">Barbados</option>
                                    <option value="BY">Belarus</option>
                                    <option value="BE">Belgium</option>
                                    <option value="BZ">Belize</option>
                                    <option value="BJ">Benin</option>
                                    <option value="BM">Bermuda</option>
                                    <option value="BT">Bhutan</option>
                                    <option value="BO">Bolivia</option>
                                    <option value="BA">Bosnia &amp; Herzegovina</option>
                                    <option value="BW">Botswana</option>
                                    <option value="BR">Brazil</option>
                                    <option value="IO">British Indian Ocean Territory</option>
                                    <option value="VG">British Virgin Islands</option>
                                    <option value="BN">Brunei</option>
                                    <option value="BG">Bulgaria</option>
                                    <option value="BF">Burkina Faso</option>
                                    <option value="BI">Burundi</option>
                                    <option value="KH">Cambodia</option>
                                    <option value="CM">Cameroon</option>
                                    <option value="CA">Canada</option>
                                    <option value="CV">Cape Verde</option>
                                    <option value="BQ">Caribbean Netherlands</option>
                                    <option value="KY">Cayman Islands</option>
                                    <option value="CF">Central African Republic</option>
                                    <option value="TD">Chad</option>
                                    <option value="CL">Chile</option>
                                    <option value="CN">China</option>
                                    <option value="CX">Christmas Island</option>
                                    <option value="CC">Cocos (Keeling) Islands</option>
                                    <option value="CO">Colombia</option>
                                    <option value="KM">Comoros</option>
                                    <option value="CG">Congo - Brazzaville</option>
                                    <option value="CD">Congo - Kinshasa</option>
                                    <option value="CK">Cook Islands</option>
                                    <option value="CR">Costa Rica</option>
                                    <option value="CI">Côte d’Ivoire</option>
                                    <option value="HR">Croatia</option>
                                    <option value="CW">Curaçao</option>
                                    <option value="CY">Cyprus</option>
                                    <option value="CZ">Czechia</option>
                                    <option value="DK">Denmark</option>
                                    <option value="DJ">Djibouti</option>
                                    <option value="DM">Dominica</option>
                                    <option value="DO">Dominican Republic</option>
                                    <option value="EC">Ecuador</option>
                                    <option value="EG">Egypt</option>
                                    <option value="SV">El Salvador</option>
                                    <option value="GQ">Equatorial Guinea</option>
                                    <option value="ER">Eritrea</option>
                                    <option value="EE">Estonia</option>
                                    <option value="SZ">Eswatini</option>
                                    <option value="ET">Ethiopia</option>
                                    <option value="FK">Falkland Islands</option>
                                    <option value="FO">Faroe Islands</option>
                                    <option value="FJ">Fiji</option>
                                    <option value="FI">Finland</option>
                                    <option value="FR">France</option>
                                    <option value="GF">French Guiana</option>
                                    <option value="PF">French Polynesia</option>
                                    <option value="TF">French Southern Territories</option>
                                    <option value="GA">Gabon</option>
                                    <option value="GM">Gambia</option>
                                    <option value="GE">Georgia</option>
                                    <option value="DE">Germany</option>
                                    <option value="GH">Ghana</option>
                                    <option value="GI">Gibraltar</option>
                                    <option value="GR">Greece</option>
                                    <option value="GL">Greenland</option>
                                    <option value="GD">Grenada</option>
                                    <option value="GP">Guadeloupe</option>
                                    <option value="GT">Guatemala</option>
                                    <option value="GG">Guernsey</option>
                                    <option value="GN">Guinea</option>
                                    <option value="GW">Guinea-Bissau</option>
                                    <option value="GY">Guyana</option>
                                    <option value="HT">Haiti</option>
                                    <option value="HN">Honduras</option>
                                    <option value="HK">Hong Kong SAR</option>
                                    <option value="HU">Hungary</option>
                                    <option value="IS">Iceland</option>
                                    <option value="IN">India</option>
                                    <option value="ID">Indonesia</option>
                                    <option value="IQ">Iraq</option>
                                    <option value="IE">Ireland</option>
                                    <option value="IM">Isle of Man</option>
                                    <option value="IL">Israel</option>
                                    <option value="IT">Italy</option>
                                    <option value="JM">Jamaica</option>
                                    <option value="JP">Japan</option>
                                    <option value="JE">Jersey</option>
                                    <option value="JO">Jordan</option>
                                    <option value="KZ">Kazakhstan</option>
                                    <option value="KE">Kenya</option>
                                    <option value="KI">Kiribati</option>
                                    <option value="XK">Kosovo</option>
                                    <option value="KW">Kuwait</option>
                                    <option value="KG">Kyrgyzstan</option>
                                    <option value="LA">Laos</option>
                                    <option value="LV">Latvia</option>
                                    <option value="LB">Lebanon</option>
                                    <option value="LS">Lesotho</option>
                                    <option value="LR">Liberia</option>
                                    <option value="LY">Libya</option>
                                    <option value="LI">Liechtenstein</option>
                                    <option value="LT">Lithuania</option>
                                    <option value="LU">Luxembourg</option>
                                    <option value="MO">Macao SAR</option>
                                    <option value="MG">Madagascar</option>
                                    <option value="MW">Malawi</option>
                                    <option value="MY">Malaysia</option>
                                    <option value="MV">Maldives</option>
                                    <option value="ML">Mali</option>
                                    <option value="MT">Malta</option>
                                    <option value="MQ">Martinique</option>
                                    <option value="MR">Mauritania</option>
                                    <option value="MU">Mauritius</option>
                                    <option value="YT">Mayotte</option>
                                    <option value="MX">Mexico</option>
                                    <option value="MD">Moldova</option>
                                    <option value="MC">Monaco</option>
                                    <option value="MN">Mongolia</option>
                                    <option value="ME">Montenegro</option>
                                    <option value="MS">Montserrat</option>
                                    <option value="MA">Morocco</option>
                                    <option value="MZ">Mozambique</option>
                                    <option value="MM">Myanmar (Burma)</option>
                                    <option value="NA">Namibia</option>
                                    <option value="NR">Nauru</option>
                                    <option value="NP">Nepal</option>
                                    <option value="NL">Netherlands</option>
                                    <option value="NC">New Caledonia</option>
                                    <option value="NZ">New Zealand</option>
                                    <option value="NI">Nicaragua</option>
                                    <option value="NE">Niger</option>
                                    <option value="NG">Nigeria</option>
                                    <option value="NU">Niue</option>
                                    <option value="NF">Norfolk Island</option>
                                    <option value="MK">North Macedonia</option>
                                    <option value="NO">Norway</option>
                                    <option value="OM">Oman</option>
                                    <option value="PK">Pakistan</option>
                                    <option value="PS">Palestinian Territories</option>
                                    <option value="PA">Panama</option>
                                    <option value="PG">Papua New Guinea</option>
                                    <option value="PY">Paraguay</option>
                                    <option value="PE">Peru</option>
                                    <option value="PH">Philippines</option>
                                    <option value="PN">Pitcairn Islands</option>
                                    <option value="PL">Poland</option>
                                    <option value="PT">Portugal</option>
                                    <option value="QA">Qatar</option>
                                    <option value="RE">Réunion</option>
                                    <option value="RO">Romania</option>
                                    <option value="RU">Russia</option>
                                    <option value="RW">Rwanda</option>
                                    <option value="WS">Samoa</option>
                                    <option value="SM">San Marino</option>
                                    <option value="ST">São Tomé &amp; Príncipe</option>
                                    <option value="SA">Saudi Arabia</option>
                                    <option value="SN">Senegal</option>
                                    <option value="RS">Serbia</option>
                                    <option value="SC">Seychelles</option>
                                    <option value="SL">Sierra Leone</option>
                                    <option value="SG">Singapore</option>
                                    <option value="SX">Sint Maarten</option>
                                    <option value="SK">Slovakia</option>
                                    <option value="SI">Slovenia</option>
                                    <option value="SB">Solomon Islands</option>
                                    <option value="SO">Somalia</option>
                                    <option value="ZA">South Africa</option>
                                    <option value="GS">South Georgia &amp; South Sandwich Islands</option>
                                    <option value="KR">South Korea</option>
                                    <option value="SS">South Sudan</option>
                                    <option value="ES">Spain</option>
                                    <option value="LK">Sri Lanka</option>
                                    <option value="BL">St Barthélemy</option>
                                    <option value="SH">St Helena</option>
                                    <option value="KN">St Kitts &amp; Nevis</option>
                                    <option value="LC">St Lucia</option>
                                    <option value="MF">St Martin</option>
                                    <option value="PM">St Pierre &amp; Miquelon</option>
                                    <option value="VC">St Vincent &amp; the Grenadines</option>
                                    <option value="SD">Sudan</option>
                                    <option value="SR">Suriname</option>
                                    <option value="SJ">Svalbard &amp; Jan Mayen</option>
                                    <option value="SE">Sweden</option>
                                    <option value="CH">Switzerland</option>
                                    <option value="TW">Taiwan</option>
                                    <option value="TJ">Tajikistan</option>
                                    <option value="TZ">Tanzania</option>
                                    <option value="TH">Thailand</option>
                                    <option value="TL">Timor-Leste</option>
                                    <option value="TG">Togo</option>
                                    <option value="TK">Tokelau</option>
                                    <option value="TO">Tonga</option>
                                    <option value="TT">Trinidad &amp; Tobago</option>
                                    <option value="TA">Tristan da Cunha</option>
                                    <option value="TN">Tunisia</option>
                                    <option value="TR">Turkey</option>
                                    <option value="TM">Turkmenistan</option>
                                    <option value="TC">Turks &amp; Caicos Islands</option>
                                    <option value="TV">Tuvalu</option>
                                    <option value="UM">US Outlying Islands</option>
                                    <option value="UG">Uganda</option>
                                    <option value="UA">Ukraine</option>
                                    <option value="AE">United Arab Emirates</option>
                                    <option value="GB">United Kingdom</option>
                                    <option value="US">United States</option>
                                    <option value="UY">Uruguay</option>
                                    <option value="UZ">Uzbekistan</option>
                                    <option value="VU">Vanuatu</option>
                                    <option value="VA">Vatican City</option>
                                    <option value="VE">Venezuela</option>
                                    <option value="VN">Vietnam</option>
                                    <option value="WF">Wallis &amp; Futuna</option>
                                    <option value="EH">Western Sahara</option>
                                    <option value="YE">Yemen</option>
                                    <option value="ZM">Zambia</option>
                                    <option value="ZW">Zimbabwe</option>
                                </FormSelect>

                                {shop?.shop_domain && (
                                    <FormAddressLookup domain={shop.shop_domain} onSelect={setVerifiedAddress}/>
                                )}

                                <div className={form.fields.validated_address_id ? "visible" : "collapse"}>
                                    <div className="grid col-span-1 gap-[12px]">
                                        <FormInput
                                            label="Address"
                                            type="text"
                                            name="address1"
                                            id="address1"
                                            required={true}
                                            aria-required="true"
                                            value={form.fields.address1}
                                            onChange={form.handleChange("address1")}
                                            disabled={true}
                                        />
                                        <FormInput
                                            label="Apartment, suite, etc (optional)"
                                            type="text"
                                            name="address2"
                                            id="address2"
                                            value={form.fields.address2}
                                            onChange={form.handleChange("address2")}
                                            disabled={true}
                                        />
                                        <div className="grid grid-cols-2 gap-[12px]">
                                            <FormInput
                                                label="City"
                                                type="text"
                                                name="city"
                                                id="city"
                                                required={true}
                                                aria-required="true"
                                                value={form.fields.city}
                                                onChange={form.handleChange("city")}
                                                disabled={true}
                                            />
                                            <FormInput
                                                label="Postcode"
                                                type="text"
                                                name="zip"
                                                id="zip"
                                                required={true}
                                                aria-required="true"
                                                value={form.fields.zip}
                                                onChange={form.handleChange("zip")}
                                                disabled={true}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="flex justify-between mt-[1.5em]">
                                    <a href="/cart">
                                        <svg viewBox="0 0 10 10" className="fill-current">
                                            <path d="M8 1L7 0 3 4 2 5l1 1 4 4 1-1-4-4"/>
                                        </svg>
                                        <span>Return to cart</span>
                                    </a>
                                    <button
                                        type="submit"
                                        className={form.fields.validated_address_id ? "btn visible" : "collapse"}
                                        name="continue"
                                    >
                                        Continue
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                    <div id="widget-container"></div>
                </div>
                <aside className="sidebar">
                    {cart &&
                        cart.items.map((x, i) => (
                            <div key={i} className="flex items-center gap-3 py-3">
                                <div className="flex-none">
                                    <div
                                        className="relative flex items-center justify-center border
                                            border-gray-300 bg-white w-16 h-16"
                                    >
                                        <img src={x.image} alt="" className="max-w-full max-h-full"/>
                                        <div
                                            className="absolute -top-4 right-0 transform
                                                translate-x-1/2 translate-y-1/2 w-4 h-4 bg-gray-500
                                                text-white rounded-full text-center text-xs"
                                        >
                                            {x.quantity}
                                        </div>
                                    </div>
                                </div>
                                <div className="grow">{unescape(x.title)}</div>
                                <div className="flex-none">
                                    {cart?.currency === "GBP" && "£"}
                                    {(x.price ? x.price / 100 : 0).toFixed(2)}
                                </div>
                            </div>
                        ))}
                    <hr/>
                    <div className="flex py-3">
                        <div className="w-6/12">Subtotal</div>
                        <div className="w-6/12 text-right">
                            {cart?.currency === "GBP" && "£"}
                            {(cart?.items_subtotal_price ? cart?.items_subtotal_price / 100 : 0).toFixed(2)}
                        </div>
                    </div>
                    <div className="flex py-3">
                        <div className="w-6/12">Discounts</div>
                        <div className="w-6/12 text-right text-xs text-gray-500">Calculated at next step</div>
                    </div>
                    <div className="flex py-3">
                        <div className="w-6/12">Shipping</div>
                        <div className="w-6/12 text-right text-xs text-gray-500">Calculated at next step</div>
                    </div>
                    <hr/>
                    <div className="flex py-3">
                        <div className="w-6/12 font-semibold">Total</div>
                        <div className="w-6/12 text-right">
                            <span className="text-xs text-gray-500">
                                {cart?.currency} {"   "}
                            </span>
                            <span className="text-2xl">
                                {cart?.currency === "GBP" && "£"}
                                {(cart?.items_subtotal_price ? cart?.items_subtotal_price / 100 : 0).toFixed(2)}
                            </span>
                        </div>
                    </div>
                </aside>

                {loading && (
                    <div className="relative z-10" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-20 transition-opacity"></div>

                        <div className="fixed inset-0 z-10 overflow-y-auto">
                            <div
                                className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                                <span className="relative flex h-20 w-20">
                                    <span
                                        className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400
                                    opacity-75"
                                    ></span>
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {indeterminateNotification && (
                    <div className="relative z-10" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

                        <div className="fixed inset-0 z-10 overflow-y-auto">
                            <div
                                className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                                <div
                                    className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl
                                transition-all sm:my-8 sm:w-full sm:max-w-lg"
                                >
                                    <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                        <div className="sm:flex sm:items-start">
                                            <div
                                                className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center
                                            rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10"
                                            >
                                                <svg
                                                    className="h-6 w-6 text-red-600"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    strokeWidth="1.5"
                                                    stroke="currentColor"
                                                    aria-hidden="true"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73
                                                        0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697
                                                        16.126zM12 15.75h.007v.008H12v-.008z"
                                                    />
                                                </svg>
                                            </div>
                                            <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                                <h3 className="text-base font-semibold leading-6 text-gray-900"
                                                    id="modal-title">
                                                    Unable to verify your age
                                                </h3>
                                                <div className="mt-2">
                                                    <p className="text-sm text-gray-500">
                                                        You will need to verify your age later - you will receive
                                                        details on how to do this after ordering.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                        <button
                                            type="button"
                                            className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm
                                            font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                                            onClick={continueToCheckout}
                                        >
                                            Checkout without age verification
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {showOneIdFailedNotification && (
                    <div className="relative z-10" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

                        <div className="fixed inset-0 z-10 overflow-y-auto">
                            <div
                                className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                                <div
                                    className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl
                                transition-all sm:my-8 sm:w-full sm:max-w-lg"
                                >
                                    <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                        <div className="sm:flex sm:items-start">
                                            <div
                                                className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center
                                            rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10"
                                            >
                                                <svg
                                                    className="h-6 w-6 text-red-600"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    strokeWidth="1.5"
                                                    stroke="currentColor"
                                                    aria-hidden="true"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73
                                                        0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697
                                                        16.126zM12 15.75h.007v.008H12v-.008z"
                                                    />
                                                </svg>
                                            </div>
                                            <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                                <h3 className="text-base font-semibold leading-6 text-gray-900"
                                                    id="modal-title">
                                                    18+ Required
                                                </h3>
                                                <div className="mt-2">
                                                    <p className="text-sm text-gray-500">
                                                        You need to be 18 or over to purchase this product. Please
                                                        remove age restricted
                                                        product from cart and try again.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                        <button
                                            type="button"
                                            className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm
                                            font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                                            onClick={backToCart}
                                        >
                                            Edit cart
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default App;
